///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { PolygonFeature } from './polygon-feature';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthProjectUserSessionService } from '../../engine/project-user-session.service';
import { PositionDTO } from '../../../../generated/shared-types.model';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable({
    providedIn: 'root'
})
export class PolygonsService {
    public isShowStoredPolygons: boolean;
    private readonly storedPolygonsMap: Map<number, PolygonFeature>;

    constructor(private readonly projectSessionService: GtthProjectUserSessionService) {
        this.storedPolygonsMap = new Map();
    }

    public parseStoredPolygons(polygons: Record<string, PositionDTO[]>, historyService: GtthHistoryService): void {
        this.storedPolygonsMap.clear();

        if (!isDefined(polygons)) {
            return;
        }

        Object.keys(polygons).forEach((polygonIndex: string) => {
            const intKey = parseInt(polygonIndex, 10);
            this.storedPolygonsMap.set(intKey, new PolygonFeature(intKey, polygons[polygonIndex], historyService));
        });
    }

    public storedPolygonsToJSON(): Record<string, PositionDTO[]> {
        const storedPolygons = {};
        this.storedPolygonsMap.forEach((polygon: PolygonFeature, polygonIndex: number) => {
            storedPolygons[polygonIndex] = polygon.getPath();
        });
        return storedPolygons;
    }

    public restoreDefaultPolygonsOptions(): void {
        this.storedPolygonsMap.forEach((polygon: PolygonFeature) => {
            polygon.restoreDefaultOptions();
        });
    }

    public overridePolygonsOptions(options: google.maps.PolygonOptions): void {
        this.storedPolygonsMap.forEach((polygon: PolygonFeature) => {
            polygon.overrideOptions(options);
        });
    }

    public hidePolygons(): void {
        this.storedPolygonsMap.forEach((polygon: PolygonFeature) => {
            polygon.hide();
        });
    }

    public showPolygons(): void {
        if (this.isShowStoredPolygons) {
            this.storedPolygonsMap.forEach((polygon) => {
                polygon.draw();
            });
        }
    }

    public savePolygon(historyService: GtthHistoryService): number {
        const path = historyService.selectAreaService.getSelectedAreaPath().map((point) => point.toPositionDto());
        return this.createAndStorePolygon(path, historyService).getPolygonIndex();
    }

    public toggleStoredPolygons(): void {
        this.projectSessionService.setShowPolygons(this.isShowStoredPolygons);
        if (this.isShowStoredPolygons) {
            this.showPolygons();
        } else {
            this.hidePolygons();
        }
    }

    public getStoredPolygon(polygonIndex: number): PolygonFeature | undefined {
        return this.storedPolygonsMap.get(polygonIndex);
    }

    public getStoredPolygonsPaths(): { latitude: number; longitude: number }[][] {
        const polygons = [];
        this.storedPolygonsMap.forEach((polygon) => polygons.push(polygon.getPath()));
        return polygons;
    }

    public clearPolygon(polygonIndex?: number): void {
        if (isDefined(polygonIndex)) {
            if (this.storedPolygonsMap.has(polygonIndex)) {
                this.storedPolygonsMap.get(polygonIndex).hide();
                this.storedPolygonsMap.delete(polygonIndex);
            }
        } else {
            this.storedPolygonsMap.forEach((polygon) => polygon.hide());
            this.storedPolygonsMap.clear();
        }
    }

    private createAndStorePolygon(path: PositionDTO[], historyService: GtthHistoryService): PolygonFeature {
        const newId = this.generatePolygonId();

        historyService.clearSelectArea(false);

        if (!this.isShowStoredPolygons) {
            this.isShowStoredPolygons = true;
            this.toggleStoredPolygons();
        }

        const polygon = new PolygonFeature(newId, path, historyService);
        this.storedPolygonsMap.set(newId, polygon);
        polygon.draw();
        return polygon;
    }

    private generatePolygonId(): number {
        const idsList = [];
        this.storedPolygonsMap.forEach((_polygon, key) => {
            idsList.push(key);
        });
        idsList.sort((a, b) => a - b);
        return idsList.length > 0 ? idsList[idsList.length - 1] + 1 : 0;
    }
}
